import { AudienceCategoryItem } from '@digiseg-labs/api-client';
import { Box } from '@radix-ui/themes';
import { MutableRefObject } from 'react';
import AudienceCategorySection from './AudienceCategorySection';
import ShareMenu, { BuildShareUrlOptions } from './ShareMenu';
import { CardEmptyState, CategoryEmptyState, SkeletonCategory } from './AudienceEmptyStates';
import { AudiencePagination } from './AudiencePagination';
import { CountryState, PlatformState, CategoryState, PaginationState } from '../../types';

type Props = {
  isLoading: boolean;
  shouldShowInitialSkeleton: boolean;
  categories: AudienceCategoryItem[];
  countryState: CountryState;
  platformState: PlatformState;
  categoryState: CategoryState;
  paginationState: PaginationState;
  buildShareUrl: (options: BuildShareUrlOptions) => string;
  categoryRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  loadMoreSentinelRef: (node: HTMLDivElement | null) => void;
};

const AudienceCategoriesView = ({
  isLoading,
  shouldShowInitialSkeleton,
  categories,
  countryState,
  platformState,
  categoryState,
  paginationState,
  buildShareUrl,
  categoryRefs,
  loadMoreSentinelRef,
}: Props) => {
  const visibleCategories = categories.filter((category) => {
    const isFocused = !!categoryState.activeCategory && category.code === categoryState.activeCategory.code;

    if (categoryState.activeCategory && !isFocused) {
      return false;
    }

    return (category.audiences?.length ?? 0) > 0;
  });

  return (
  <>
    {shouldShowInitialSkeleton && (
      <Box>
        <SkeletonCategory />
        <SkeletonCategory />
        <SkeletonCategory />
      </Box>
    )}

    {!shouldShowInitialSkeleton && !visibleCategories.length && (
      categoryState.activeCategory ? (
        <CategoryEmptyState categoryName={categoryState.activeCategory.display_name} onClear={categoryState.onClearCategory || (() => {})} 
        />
      ) : (
        <CardEmptyState hasCountry={!!countryState.country} />
      )
    )}
    
    {visibleCategories.map((category) => {
      const isFocused = !!categoryState.activeCategory && category.code === categoryState.activeCategory.code;

      return (
      <AudienceCategorySection
        key={category.code}
        category={category}
        countries={countryState.countries || []}
        platforms={platformState.platforms || []}
        selectedCountry={countryState.country}
        selectedPlatform={platformState.platform}
        onCountrySelected={countryState.onCountryChange || (() => {})}
        onPlatformSelected={platformState.onPlatformChange || (() => {})}
        onCategoryFocus={categoryState.onCategorySelect}
        isFocused={isFocused}
        disableAudienceLimit={isFocused}
        shareControls={(
          <ShareMenu
            target={{ type: 'category', code: category.code, name: category.display_name }}
            selectedCountry={countryState.country}
            selectedPlatform={platformState.platform}
            buildShareUrl={buildShareUrl}
          />
        )}
        buildShareUrl={buildShareUrl}
        openAudienceCode={categoryState.openAudienceCode || null}
        onModalOpenChange={categoryState.onAudienceModalChange || (() => {})}
        ref={(node) => {
          if (node) {
            categoryRefs.current[category.code] = node;
          } else {
            delete categoryRefs.current[category.code];
          }
        }}
      />
      );
    })}
    <AudiencePagination loadMoreSentinelRef={loadMoreSentinelRef} rangeTruncated={paginationState.rangeTruncated || false} />
  </>
  );
}

export default AudienceCategoriesView;
